"use client";
import React, { useState } from "react";
import {
  Alert,
  CircularProgress,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Snackbar,
} from "@mui/material";

import s from "./ConsultationModal.module.scss";
import { emailjsSend } from "../emailjsConfig";
import Thankssvg from "../assets/Thankssvg.svg";

const ConsultationModal = ({ open, handleClose }) => {
  /**──────────────────
   * ‣ Form state
   * ──────────────────*/
  const [value, setValue] = useState("Residential");
  const [consult, setConsult] = useState({
    radio_buttons_group: "Residential",
  });

  /** Validation state */
  const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
  const postalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

  /** UI helpers */
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  /**──────────────────
   * ‣ Handlers
   * ──────────────────*/
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    /* 1. update form object */
    if (name === "radio_buttons_group") {
      setValue(value);
    }
    setConsult((prev) => ({ ...prev, [name]: value }));
  };

  const handleToastClose = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    const missingLabels = [
      { key: "full_name", label: "Full Name" },
      { key: "business_name", label: "Business Name" },
      { key: "phone", label: "Phone Number" },
      { key: "email", label: "Email" },
      { key: "Address", label: "Address" },
      { key: "postal_code", label: "Postal Code" },
      { key: "date", label: "Preferred Date" },
      { key: "time", label: "Preferred Time" },
      { key: "radio_buttons_group", label: "Service Type" },
      { key: "message", label: "Message" },
    ].filter(({ key }) => !consult[key]);

    const issues = [];
    if (missingLabels.length) {
      issues.push(
        `Missing: ${missingLabels.map(({ label }) => label).join(", ")}.`,
      );
    }

    if (consult.email && !emailRegex.test(consult.email)) {
      issues.push("Please enter a valid email address.");
    }

    if (consult.postal_code && !postalRegex.test(consult.postal_code)) {
      issues.push("Please enter a valid postal code (e.g., A1A 1A1).");
    }

    if (issues.length) {
      setToast({
        open: true,
        message: issues.join(" • "),
        severity: "error",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSending(true);
    const res = await emailjsSend(consult, "template_8zhh6tf");
    setIsSending(false);

    if (res.ok) {
      setSuccess(true);
      setToast({
        open: true,
        message: "Your consultation request was sent successfully.",
        severity: "success",
      });
    } else {
      setToast({
        open: true,
        message: res.error || "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  /**──────────────────
   * ‣ Render
   * ──────────────────*/
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'>
      <div className={s.insideModal}>
        {/* ───── Left column ───── */}
        <div className={s.leftSide}>
          <button className={s.whiteButton} onClick={handleClose}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='26'
              height='27'
              viewBox='0 0 26 27'
              fill='none'>
              <path
                d='M19.2004 7.1001L6.40039 19.9001M6.40039 7.1001L19.2004 19.9001'
                stroke='white'
                strokeWidth='1.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            Close
          </button>

          <h1>Let’s Schedule Your Consultation</h1>
          <p>
            Ready to illuminate your space? Fill out the form to book your
            consultation and get started on brightening up your place.
          </p>
        </div>

        {/* ───── Right column (states) ───── */}
        {success ? (
          <div className={s.thanks}>
            <img src={Thankssvg} alt='Thank you' />
            <h3>Thank you for submitting</h3>
            <p>Your message has been sent!</p>
          </div>
        ) : isSending ? (
          <div className={s.Submitting}>
            <CircularProgress style={{ width: 50, height: 50 }} />
            Submitting…
          </div>
        ) : (
          <div className={s.rightSide}>
            {/* ── Full name + business ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Full Name</p>
                <input
                  placeholder='Enter your full name'
                  type='text'
                  name='full_name'
                  id='full_name'
                  onChange={handleFormChange}
                />
              </div>

              <div className={s.input}>
                <p>Business Name</p>
                <input
                  placeholder='Enter your business name'
                  type='text'
                  name='business_name'
                  id='business_name'
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Email + phone ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>E‑mail Address</p>
                <input
                  placeholder='Enter your e‑mail address'
                  type='email'
                  name='email'
                  id='email'
                  onChange={handleFormChange}
                />
              </div>

              <div className={s.input}>
                <p>Phone Number</p>
                <input
                  placeholder='Enter your phone number'
                  type='text'
                  name='phone'
                  id='phone'
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Address + postal code ── */}
            <div className={`${s.rowInputs} ${s.addrZip}`}>
              <div className={`${s.input} ${s.addressInput}`}>
                <p>Address</p>
                <input
                  placeholder='123 Main St, Toronto, ON'
                  type='text'
                  name='Address'
                  id='Address'
                  onChange={handleFormChange}
                />
              </div>

              <div className={`${s.input} ${s.zipInput}`}>
                <p>Postal Code</p>
                <input
                  placeholder='A1A 1A1'
                  type='text'
                  name='postal_code'
                  id='postal_code'
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Service type radio group ── */}
            <div className={s.radioGroup}>
              <p>Service Type</p>
              <RadioGroup
                className={s.groupWrapper}
                name='radio_buttons_group'
                value={value}
                onChange={handleFormChange}>
                {["Residential", "Commercial", "Industrial"].map((opt) => (
                  <FormControlLabel
                    key={opt}
                    className={value === opt ? s.radio1 : s.radio2}
                    value={opt}
                    control={<Radio />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
            </div>

            {/* ── Date + time ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Preferred Dates &amp; Times</p>
                <div className={s.selectWrapper}>
                  <input
                    onFocus={(e) => (e.target.type = "date")}
                    type='text'
                    placeholder='Select Date'
                    name='date'
                    id='date'
                    onChange={handleFormChange}
                  />
                  <input
                    onFocus={(e) => (e.target.type = "time")}
                    type='text'
                    placeholder='Select Time'
                    name='time'
                    id='time'
                    onChange={handleFormChange}
                  />
                </div>
              </div>
            </div>

            {/* ── Message ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Message</p>
                <textarea
                  rows={5}
                  placeholder='Please provide details about the service request'
                  name='message'
                  id='message'
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Submit ── */}
            <button
              className={`${s.redButton} ${isSending ? "disabled" : ""}`}
              type='submit'
              disabled={isSending}
              onClick={handleSubmit}>
              Submit
            </button>
          </div>
        )}
        <Snackbar
          open={toast.open}
          autoHideDuration={5000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleToastClose}
            severity={toast.severity}
            sx={{ width: "100%" }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </Modal>
  );
};

export default ConsultationModal;
